import React, { useEffect, useRef, useState } from "react";
import SelectAsync from "react-select/async";
import dayjs from "dayjs";
import Form from "./Form";
import { apiUrl } from "../../utils/apiUrl";
import { formatCurrency, toast, validarPINSeguridad } from "../../utils/helpers";
import { useRouteMatch } from "../../utils/router";
import { apiRequest } from "../../hooks/apiRequest";
import Loader from "../general/Loader";
import { postRequest } from "../../utils/request";
import Modal from "../general/Modal";

/**
 * Los errores de validación llegan como {campo: [mensajes]}; los demás, como
 * texto. Antes se pasaba el objeto directo al toast.
 */
const mensajeError = (response) => {
    const data = response?.data;
    if (typeof data === "string") return data;

    const errores = data?.message ?? data;
    if (typeof errores === "string") return errores;

    const primero = errores && Object.values(errores)[0];
    return Array.isArray(primero) ? primero[0] : "No se pudo completar la operación.";
};

const formatFecha = (fecha) => (fecha ? dayjs(fecha).format("DD/MM/YYYY") : "");

const Documentos = ({ cheque }) => {
    const documentos = cheque.documentos || [];

    if (!documentos.length) {
        return null;
    }

    return (
        <div className="m-t-20">
            <h5 className="m-b-10">
                {cheque.tipo === "INGRESO" ? "Facturas cobradas" : "Compras pagadas"} con este cheque
            </h5>
            <table className="table table-compact">
                <thead>
                    <tr>
                        <th>Documento</th>
                        <th>Concepto</th>
                        <th className="text-right">Monto</th>
                        <th className="text-right">Pendiente</th>
                        <th className="text-center">Pago aplicado</th>
                    </tr>
                </thead>
                <tbody>
                    {documentos.map((documento) => (
                        <tr key={documento.id}>
                            <td>{documento.numero}</td>
                            <td>{documento.concepto}</td>
                            <td className="text-right">{formatCurrency(documento.monto)}</td>
                            <td className="text-right">{formatCurrency(documento.porPagar ?? documento.porCobrar ?? 0)}</td>
                            <td className="text-center">
                                {documento.pagado
                                    ? <span className="badge badge-success">Sí</span>
                                    : <span className="badge badge-secondary">No</span>}
                            </td>
                        </tr>
                    ))}
                    <tr>
                        <td colSpan={2} className="text-right"><strong>Total del cheque</strong></td>
                        <td className="text-right"><strong>{formatCurrency(cheque.monto)}</strong></td>
                        <td colSpan={2}></td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const Edit = ({getCheques, toggle}) => {

    const [state, setState] = useState({
        cheque: {},
        loading: true,
        saving: false,
    });

    const [errors, setErrors] = useState({});
    const [afectarCuentas, setAfectarCuentas] = useState(true);

    const [showPINModal, setShowPINModal] = useState(false);
    const [pinSeguridad, setPinSeguridad] = useState("");
    const [accion, setAccion] = useState("");
    // Un cheque cobrado sin cuenta destino la necesita para poder validarse:
    // es la cuenta donde se deposita.
    const [cuentaValidar, setCuentaValidar] = useState(null);

    const formRef = useRef();

    const match = useRouteMatch();

    const getCheque = async () => {
        const id = match.params.id;
        const cheque = await apiRequest(null, 'cheques/' + id, 'get');
        if (cheque.data) {
            setState({
                ...state,
                cheque: cheque.data,
                loading: false,
            });
        }
    }

    const { cheque, loading, saving } = state;
    const faltaCuenta = !cheque.cuenta;

    const getCuentaOptions = async (buscar) => {
        const table = await apiRequest({}, "cuentas?buscar=" + buscar, 'get');
        if (table.data) {
            return table.data.map(item => ({value: item.id, label: item.nombre}));
        }
    };

    const checkPINSeguridad = async () => {
        if (accion === "validar" && faltaCuenta && !cuentaValidar) {
            toast("Seleccione la cuenta donde se deposita el cheque.", "error");
            return;
        }

		if (await validarPINSeguridad(pinSeguridad)) {
			setPinSeguridad("");
			setShowPINModal(false);

            if (accion === 'editar') {
                formRef.current.dispatchEvent(
                    new Event("submit", { bubbles: true, cancelable: true })
                );
            } else if(accion === "anular") {
                anularCheque(cheque.id);
            } else if(accion === "validar") {
                validarCheque(cheque.id);
            }

			return;
		}

		toast("PIN incorrecto, intente de nuevo", "error");
	};

    const pedirPIN = (nuevaAccion) => (event) => {
        event.preventDefault();

        setAccion(nuevaAccion);
        setShowPINModal(true);
    };

    const editCheque = async (event) => {
        event.preventDefault();

        setState({ ...state, saving: true });

        const data = new FormData(event.target);
        const id = match.params.id;

        const response = await apiRequest(data, `cheques/${id}`);

        if (response.success) {
            toast('Cheque actualizado.');
            toggle();
            getCheques();
        }

        if (response.code === 422) {
            setErrors(response.data.message);
            toast('Error editando cheque.', 'error');
        }

        setState({ ...state, saving: false });
    };

    const ejecutar = async (url, data, mensaje) => {
        setState({ ...state, saving: true });

        const response = await postRequest(apiUrl(url), data, 'post');

        if (response.success) {
            getCheques();
            toggle();
            toast(mensaje);
            return;
        }

        toast(mensajeError(response), "error");
        setState({ ...state, saving: false });
    };

    const anularCheque = (id) => ejecutar(`cheques/${id}/anular`, new FormData(), "Cheque anulado.");

    const validarCheque = (id) => {
        const data = new FormData();
        if (cuentaValidar) {
            data.append("cuenta_id", cuentaValidar.value);
        }

        return ejecutar(`cheques/${id}/validar`, data, "Cheque validado.");
    };

    useEffect(() => {
        getCheque();
    }, []);

    useEffect(() => {
        setAfectarCuentas(state.cheque.movimientos?.length > 0)
    }, [state]);

    useEffect(() => {
        setPinSeguridad("");

        if (!showPINModal) {
            setAccion("");
        }
    }, [showPINModal]);

    if (loading) {
        return (<Loader />)
    }

    const documentos = cheque.documentos || [];
    // Un cheque histórico (datos del modelo anterior que no cuadran) es de solo lectura.
    const puedeAnular = cheque.status !== "rechazado" && !cheque.historico;
    const puedeValidar = cheque.status === "procesando" && !cheque.historico;

    return (
        <>
            <form method='post' onSubmit={editCheque} ref={formRef}>
                <input type='hidden' name='_method' value='patch' />
                <div className='popup__header'>
                    Editar Cheque
                    <div className="float-right">
                        <small className="m-r-15">Afectar cuentas contables</small>
                        <label className='switch switch-3d switch-success mr-3'>
                            <input type='hidden' name='afectar_cuentas' value='no' />
                            <input
                                key={cheque.id}
                                type='checkbox'
                                className='switch-input'
                                id='afectar_cuentas'
                                name='afectar_cuentas'
                                value='yes'
                                checked={afectarCuentas}
                                disabled={cheque.status !== "procesando" || documentos.length > 0}
                                onChange={() => setAfectarCuentas(!afectarCuentas)}
                            />
                            <span className='switch-label' />
                            <span className='switch-handle' />
                        </label>
                    </div>
                </div>
                <div className='popup__body'>
                    {cheque.historico && (
                        <div className="alert alert-secondary m-b-15">
                            <strong>Cheque histórico.</strong> Es anterior al rediseño de cheques y sus datos no
                            cuadran con las reglas actuales, así que es de solo lectura: no se puede validar ni anular.
                        </div>
                    )}
                    {(cheque.validadoAt || cheque.rechazadoAt) && (
                        <div className="alert alert-light m-b-15">
                            {cheque.validadoAt && <div><strong>Validado:</strong> {formatFecha(cheque.validadoAt)}</div>}
                            {cheque.rechazadoAt && <div><strong>Anulado:</strong> {formatFecha(cheque.rechazadoAt)}</div>}
                        </div>
                    )}
                    <Form cheque={cheque} errors={errors} afectarCuentas={afectarCuentas} isEdit={true} />
                    <Documentos cheque={cheque} />
                </div>
                <div className='popup__footer'>
                        <div className="float-left">
                            {puedeAnular && (
                                <button
                                    type='button'
                                    className='btn btn-danger mr-2'
                                    onClick={pedirPIN("anular")}>
                                    Anular
                                </button>
                            )}
                            {puedeValidar && (
                                <button
                                    type='button'
                                    className='btn btn-success'
                                    onClick={pedirPIN("validar")}>
                                    Validar
                                </button>
                            )}
                        </div>

                    <button
                        type='button'
                        onClick={toggle}
                        className='btn btn-plain'>
                        Cancelar
                    </button>

                    {saving ? (
                        <Loader type='small' />
                    ) : (
                        <button type='button' className='btn btn-primary' onClick={pedirPIN("editar")}>
                            Guardar
                        </button>
                    )}
                </div>
            </form>

            {/* <---PIN DE SEGURIDAD MODAL--- */}
            <Modal
                title="PIN de seguridad"
                show={showPINModal}
                callback={() => checkPINSeguridad()}
                acceptText="Aceptar"
                toggle={() => setShowPINModal((state) => !state)}
                size="419px"
            >
                <div className="form-group">
                    {accion === "validar" && faltaCuenta && (
                        <div className="m-b-15">
                            <label>Cuenta donde se deposita</label>
                            <SelectAsync
                                placeholder='Seleccionar...'
                                menuPosition='fixed'
                                menuPlacement='auto'
                                cacheOptions
                                defaultOptions
                                value={cuentaValidar}
                                loadOptions={getCuentaOptions}
                                onChange={setCuentaValidar}
                            />
                        </div>
                    )}
                    <label>PIN de seguridad</label>
                    <input
                        type="password"
                        className="form-control"
                        id="PIN"
                        name="pin_seguridad"
                        value={pinSeguridad}
                        onChange={(e) =>
                            setPinSeguridad(e.target.value)
                        }
                    />
                    <p
                        className="font-weight-bold"
                        style={{
                            color: "gray",
                            fontStyle: "italic",
                            lineHeight: "13px",
                            fontSize: "0.7rem",
                        }}
                    >
                        Para continuar ingrese PIN de seguridad
                    </p>
                    {accion === 'anular' && (
                        <div className="alert alert-warning mt-3">
                            <strong>Se anulará el cheque completo.</strong>
                            <ul className="m-b-0 p-l-20">
                                {cheque.status === "válido" && documentos.length > 0 && (
                                    <li>
                                        Se revierten los pagos de {documentos.length} {cheque.tipo === "INGRESO" ? "factura(s)" : "compra(s)"} y el movimiento de la cuenta.
                                    </li>
                                )}
                                {cheque.status === "válido" && documentos.length === 0 && (
                                    <li>Se revierte el movimiento de la cuenta.</li>
                                )}
                                {documentos.length > 0 && (
                                    <li>Los documentos vuelven a quedar pendientes.</li>
                                )}
                                {cheque.recibo && (
                                    <li>Se cancela el recibo de <span className="lowercase">{cheque.tipo}</span> {cheque.recibo.numeracion}.</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
}

export default Edit;
